import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';

import type { AiSuggestionsPlugin, Suggestion } from '../../aiSuggestionsPluginType';
import { aiSuggestionsPluginKey } from '../../pm-plugins/main';
import { fetchSuggestions } from './api';
import { createStaleSuggestionsUpdater } from './staleSuggestionsUpdater';

/** The suggestions currently in plugin state, in list order. */
function getCurrentSuggestions(
	api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined,
): Suggestion[] {
	const state = api?.aiSuggestions?.sharedState.currentState();
	if (!state) {
		return [];
	}
	return state.orderedSuggestionIds
		.map((id) => state.suggestionById.get(id))
		.filter((suggestion): suggestion is Suggestion => suggestion !== undefined);
}

/**
 * Fetches the suggestions and updates the plugin state. When `shouldRegenerate`
 * is set (the default) and a `getDoc` reader is provided, freshly-fetched
 * suggestions are reconciled against the current document — stale ones are
 * regenerated — before being committed to the plugin state.
 *
 * With `regenerateOnly` the fetch is skipped and the suggestions already in
 * plugin state are reconciled instead, each stale one swapped for its
 * regenerated counterpart in place. Nothing is dispatched when nothing was
 * stale.
 */
export const updateSuggestions = async ({
	api,
	contentAri,
	signal,
	getDoc,
	shouldRegenerate = true,
	regenerateOnly = false,
}: {
	api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined;
	contentAri?: string;
	getDoc?: () => PMNode | undefined;
	/**
	 * Reconcile the suggestions already in plugin state instead of fetching. See
	 * the `regenerateOnly` option on `refreshSuggestions` for why a caller would
	 * want this over a full refresh.
	 */
	regenerateOnly?: boolean;
	shouldRegenerate?: boolean;
	signal?: AbortSignal;
}): Promise<void> => {
	if (!api) {
		return;
	}

	let suggestions: Suggestion[];

	if (regenerateOnly) {
		// Regenerating needs a doc to hash against and an ARI to regenerate under.
		if (!getDoc || !contentAri) {
			return;
		}

		const current = getCurrentSuggestions(api);
		if (current.length === 0) {
			return;
		}

		const reconciled = await createStaleSuggestionsUpdater(getDoc, contentAri, api, {
			signal,
			shouldRetainStale: true,
		})(current);

		// Same reference back means nothing was stale (or regeneration failed) —
		// skip the dispatch so we don't recompute decorations for no change.
		if (reconciled === current) {
			return;
		}

		suggestions = reconciled;
	} else {
		const fetched = await fetchSuggestions({ contentAri: contentAri ?? '', signal });

		suggestions =
			shouldRegenerate && getDoc && contentAri
				? await createStaleSuggestionsUpdater(getDoc, contentAri, api, {
						signal,
						shouldRetainStale: false,
					})(fetched)
				: fetched;
	}

	api?.core?.actions?.execute(({ tr }) => {
		tr.setMeta(aiSuggestionsPluginKey, {
			action: 'SET_SUGGESTIONS',
			suggestions,
			contentAri,
		});
		return tr;
	});
};
